import {
  Actions,
  ArrowTopRightOnSquareIcon,
  Badge,
  ClipboardDocumentIcon,
  FolderIcon,
  formatTimeDate,
  LinkIcon,
  SwatchIcon,
  success,
} from "@";
import type { ElementType } from "react";
import type { LandingPage } from "../types";
import type { ColumnDef } from "@tanstack/react-table";
import TogglePublishStatus from "@/shared/components/dataTable/TogglePublish";
import { useCanShow } from "@/shared/hooks";

interface TableActionsProps {
  row: LandingPage;
  deleteHandler: (id: number) => Promise<void>;
  TogglePublished: (id: number, value: boolean) => Promise<void>;
}

const copyLink = (link: string) => {
  navigator.clipboard.writeText(link);
  success("تم النسخ");
};

export const TableActions = ({
  row,
  deleteHandler,
  TogglePublished,
}: TableActionsProps) => {
  const canManage = useCanShow("editSitePages");
  const canPreview = !!row.url && !!row.slug && !!row.isPublished;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Actions
        notEdit={!canManage}
        notDelete={!canManage}
        row={row}
        url="website/landing-pages"
        delete={deleteHandler}
      >
        {canManage && <TogglePublishStatus row={row} click={TogglePublished} />}
      </Actions>

      {canPreview && (
        <>
          <a
            href={row.url}
            target="_blank"
            rel="noopener noreferrer"
            title="فتح الصفحة"
            className="btn-white whitespace-nowrap border-gray-100"
          >
            <ArrowTopRightOnSquareIcon className="size-5" aria-hidden />
            فتح
          </a>
          <button
            type="button"
            onClick={() => copyLink(row.url)}
            title="نسخ رابط الصفحة"
            className="btn-white whitespace-nowrap border-gray-100"
          >
            <ClipboardDocumentIcon className="size-5" aria-hidden />
            نسخ الرابط
          </button>
        </>
      )}
    </div>
  );
};

// ── Cell building blocks ─────────────────────────────────────────────────────

const Dash = () => <span className="text-gray-300 dark:text-gray-600">—</span>;

const DateText = ({ value }: { value?: string }) =>
  value ? (
    <span className="text-sm tabular-nums text-gray-700 dark:text-gray-300">
      {formatTimeDate(value)}
    </span>
  ) : (
    <Dash />
  );

const TagText = ({
  icon: Icon,
  label,
}: {
  icon: ElementType;
  label?: string;
}) =>
  label ? (
    <span className="inline-flex max-w-full items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
      <Icon
        className="size-4 shrink-0 text-gray-400 dark:text-gray-500"
        aria-hidden
      />
      <span className="truncate">{label}</span>
    </span>
  ) : (
    <Dash />
  );

// ── Columns ──────────────────────────────────────────────────────────────────

export const tableColumns: ColumnDef<LandingPage>[] = [
  {
    id: "name",
    header: "الصفحة",
    cell: ({ row }) => {
      const { name, slug } = row.original;
      return (
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            {name || "—"}
          </span>
          {slug && (
            <span
              dir="ltr"
              className="inline-flex w-fit max-w-full items-center gap-1 truncate font-mono text-[11px] text-gray-400 dark:text-gray-500"
            >
              <LinkIcon className="size-3 shrink-0" aria-hidden />
              /lp/{slug}
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: "project",
    header: "المشروع",
    cell: ({ row }) => (
      <TagText icon={FolderIcon} label={row.original.projectTitle} />
    ),
  },
  {
    id: "theme",
    header: "الثيم",
    cell: ({ row }) => (
      <TagText icon={SwatchIcon} label={row.original.themeTitle} />
    ),
  },
  {
    id: "status",
    header: "الحالة",
    size: 120,
    cell: ({ row }) => {
      const { isPublished, publishedAt } = row.original;
      return (
        <div className="flex flex-col items-start gap-0.5">
          <Badge
            title={isPublished ? "تم النشر" : "مسودة"}
            color={isPublished ? "green" : "dark"}
          />
          {isPublished && publishedAt && (
            <span className="ps-1 text-[10px] tabular-nums text-gray-400 dark:text-gray-500">
              {formatTimeDate(publishedAt)}
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: "createdAt",
    header: "تاريخ الإنشاء",
    size: 150,
    cell: ({ row }) => <DateText value={row.original.createdAt} />,
  },
  {
    id: "updatedAt",
    header: "آخر تحديث",
    size: 150,
    cell: ({ row }) => <DateText value={row.original.updatedAt} />,
  },
];
